/**
 * Loads PayPal's JS SDK once per client id / currency / language.
 * The SDK renders PayPal's own checkout buttons; it never sees our prices —
 * createOrder/onApprove call our server, which sets the amount.
 */

// The SDK's global, typed loosely: only the parts we call.
export interface PayPalButtonsActions { restart?: () => void }
export interface PayPalButtons {
  render: (el: HTMLElement) => Promise<void>;
  close: () => Promise<void>;
  isEligible: () => boolean;
}
export interface PayPalNamespace {
  Buttons: (opts: {
    style?: Record<string, unknown>;
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID: string }, actions: PayPalButtonsActions) => Promise<void>;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
  }) => PayPalButtons;
}

declare global {
  interface Window { paypal?: PayPalNamespace }
}

let loading: Promise<PayPalNamespace> | null = null;
let loadedKey = "";

export function loadPayPal(clientId: string, currency: string, lang: string): Promise<PayPalNamespace> {
  const locale = lang.startsWith("en") ? "en_US" : "ru_RU";
  const key = `${clientId}|${currency}|${locale}`;
  if (loading && loadedKey === key) return loading;

  document.querySelectorAll("script[data-paypal-sdk]").forEach((s) => s.remove());
  delete window.paypal;
  loadedKey = key;
  loading = new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      "client-id": clientId,
      currency,
      intent: "capture",
      components: "buttons",
      locale,
      // Pay with the PayPal wallet or a card; no "Pay Later" offers.
      "disable-funding": "paylater",
    });
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?${params}`;
    s.async = true;
    s.dataset.paypalSdk = "1";
    s.onload = () => (window.paypal ? resolve(window.paypal) : reject(new Error("PayPal SDK missing")));
    s.onerror = () => { loading = null; reject(new Error("PayPal SDK failed to load")); };
    document.head.appendChild(s);
  });
  return loading;
}
